/**
 * Development-only logging utility
 * Only outputs to console when running in development mode
 */

/* eslint-disable no-console */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface DevLogOptions {
  prefix?: string;
  timestamp?: boolean;
  level?: LogLevel;
}

/**
 * Console log that only shows up in development mode
 * @param message - The message to log
 * @param data - Optional data to log alongside the message
 * @param options - Optional configuration for the log output
 */
export const devLog = (
  message: string, 
  data?: any, 
  options: DevLogOptions = {}
): void => {
  // Only log in development mode
  if (!import.meta.env.DEV) {
    return;
  }

  const { 
    prefix = '[DEV]', 
    timestamp = true, 
    level = 'log' 
  } = options;

  // Build the log message
  let logMessage = '';
  
  if (timestamp) {
    const now = new Date().toLocaleTimeString();
    logMessage += `${now} `;
  }
  
  if (prefix) {
    logMessage += `${prefix} `;
  }
  
  logMessage += message;

  // Log to console using the specified level
  if (data !== undefined) {
    console[level](logMessage, data);
  } else {
    console[level](logMessage);
  }
};

/**
 * Convenience methods for different log levels
 */
export const devLogInfo = (message: string, data?: any) => 
  devLog(message, data, { level: 'info', prefix: '[DEV-INFO]' });

export const devLogWarn = (message: string, data?: any) => 
  devLog(message, data, { level: 'warn', prefix: '[DEV-WARN]' });

export const devLogError = (message: string, data?: any) => 
  devLog(message, data, { level: 'error', prefix: '[DEV-ERROR]' });

export const devLogDebug = (message: string, data?: any) => 
  devLog(message, data, { level: 'debug', prefix: '[DEV-DEBUG]' });

/**
 * Performance timing utility for development
 */
export const devTime = {
  start: (label: string) => {
    if (import.meta.env.DEV) {
      console.time(`[DEV-TIMER] ${label}`);
    }
  },
  end: (label: string) => {
    if (import.meta.env.DEV) {
      console.timeEnd(`[DEV-TIMER] ${label}`);
    }
  }
};

/**
 * Group logging for better organization in dev tools
 */
export const devGroup = {
  start: (label: string, collapsed = false) => {
    if (import.meta.env.DEV) {
      if (collapsed) {
        console.groupCollapsed(`[DEV-GROUP] ${label}`);
      } else {
        console.group(`[DEV-GROUP] ${label}`);
      }
    }
  },
  end: () => {
    if (import.meta.env.DEV) {
      console.groupEnd();
    }
  }
};

/**
 * Table logging for complex data structures
 */
export const devTable = (data: any, label?: string) => {
  if (import.meta.env.DEV) {
    if (label) {
      console.log(`[DEV-TABLE] ${label}`);
    }
    console.table(data);
  }
};

export default devLog;
